// API client for Assessment Course Categories (the purchasable unit built from
// a subset of one course's content).
//
// Backend (sia-backend, /api/assessment/admin/*), envelope { data, isError,
// statusCode, errorMessage }:
//   - GET/POST /categories, PUT/DELETE /categories/{id}; the list is a page
//     { content, page, size, totalElements, totalPages, hasNext }
//   - DELETE is a soft delete (active=false + deletedAt) and the list always
//     hides soft-deleted rows
//   - GET /courses/{courseId}/content-tree?categoryId returns the course's
//     Subject > Chapter > Topic > (Sub-Topic | Video) tree with `included`
//   - PUT /categories/{id}/content-mapping replaces the mapping with the FULL
//     expanded selection (a child needs its parent in the same request, else 400)
//   - GET /courses/{courseId}/tests?categoryId + PUT /categories/{id}/test-mapping
import api from "../axiosInstance";

const BASE = "/api/assessment/admin";

const unwrap = (res) => res.data?.data;

const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  );

export const ACCESS_TYPES = ["FREE", "PREMIUM"];

// filters: courseId, isActive, search, page, size
export const listCategories = async ({ page = 0, size = 100, ...filters } = {}) => {
  const res = await api.get(`${BASE}/categories`, {
    params: cleanParams({ ...filters, page, size }),
  });
  const data = unwrap(res) || {};
  return {
    ...data,
    content: (data.content || []).map((c) => ({
      ...c,
      status: c.active ? "ACTIVE" : "INACTIVE",
      subjectCount: c.subjectCount ?? 0,
      chapterCount: c.chapterCount ?? 0,
      topicCount: c.topicCount ?? 0,
      subTopicCount: c.subTopicCount ?? 0,
      videoCount: c.videoCount ?? 0,
      courseIds: c.courseIds ?? [],
      courseNames: c.courseNames ?? [],
    })),
  };
};

// Payload: courseId (create only), name, accessType, price, durationDays,
// freePreviewChapterCount, freePreviewTopicCount, displayOrder, active,
// readinessTargetPercentage (0-100; omit to keep),
// courseIds[] (regular courses whose students see this category; omit to keep, [] to clear).
export const createCategory = (payload) => api.post(`${BASE}/categories`, payload).then(unwrap);
export const updateCategory = (id, payload) =>
  api.put(`${BASE}/categories/${id}`, payload).then(unwrap);
export const deleteCategory = (id) => api.delete(`${BASE}/categories/${id}`).then(unwrap);

export const getContentTree = (courseId, categoryId) =>
  api
    .get(`${BASE}/courses/${courseId}/content-tree`, { params: cleanParams({ categoryId }) })
    .then(unwrap);

// selection: { subjectIds, chapterIds, topicIds, subTopicIds, videoIds }
export const saveContentMapping = (categoryId, selection) =>
  api.put(`${BASE}/categories/${categoryId}/content-mapping`, selection).then(unwrap);

export const listCourseTests = (courseId, categoryId) =>
  api
    .get(`${BASE}/courses/${courseId}/tests`, { params: cleanParams({ categoryId }) })
    .then((res) => unwrap(res) || []);

export const saveTestMapping = (categoryId, testIds) =>
  api.put(`${BASE}/categories/${categoryId}/test-mapping`, { testIds }).then(unwrap);
